import { Injectable, NgZone, inject, signal } from '@angular/core';
import { environment } from '@env/environment';
import { DevBottomBarService } from '../dev-bottom-bar.service';
import { CONSOLE_LEVELS, ConsoleEntry, ConsoleLevel } from './dev-console.types';

const MAX_ENTRIES = 500;
const KEY_ENABLED_LEVELS = 'devBar.console.enabledLevels';
/** How often (ms) buffered captures are flushed to the signal.
 *  Keeps CD frequency low — prevents NG0100 feedback loops. */
const FLUSH_INTERVAL_MS = 300;

type ConsoleMethod = (...args: unknown[]) => void;

/**
 * Intercepts `console` calls when the Dev Console panel is enabled.
 *
 * IMPORTANT: when the panel is disabled we restore the *exact* original methods
 * we snapshotted at the moment of wrapping, so browser DevTools continues to
 * show accurate source locations for `console.log` calls. This is the whole
 * reason we hook/unhook on toggle instead of wrapping once forever.
 */
@Injectable({ providedIn: 'root' })
export class DevConsoleService {
  private readonly bottomBar = inject(DevBottomBarService);
  private readonly ngZone = inject(NgZone);

  private readonly _entries = signal<ConsoleEntry[]>([]);
  readonly entries = this._entries.asReadonly();

  /** Levels currently shown in the panel. Hydrated from localStorage so the
   *  user's filter choices survive a page refresh. */
  private readonly _enabledLevels = signal<Set<ConsoleLevel>>(this.loadEnabledLevels());
  readonly enabledLevels = this._enabledLevels.asReadonly();

  private isHooked = false;
  private originals: Partial<Record<ConsoleLevel, ConsoleMethod>> = {};

  /** Buffered captures — flushed to the signal every FLUSH_INTERVAL_MS. */
  private buffer: ConsoleEntry[] = [];
  private flushScheduled = false;
  /** Keys seen in the current flush window — duplicates are suppressed to
   *  break NG0100-style feedback loops where CD → error → capture → CD. */
  private recentKeys = new Set<string>();

  constructor() {
    if (environment.production) return;
    this.bottomBar.consoleVisible$.subscribe(visible => {
      if (visible) this.hook();
      else this.unhook();
    });
  }

  // ── Entry management ────────────────────────────────────────────────────────

  clear(): void {
    this._entries.update(list => list.filter(e => e.pinned));
  }

  remove(id: string): void {
    this._entries.update(list => list.filter(e => e.id !== id));
  }

  togglePin(id: string): void {
    this._entries.update(list =>
      list.map(e => e.id === id ? { ...e, pinned: !e.pinned } : e)
    );
  }

  toggleLevel(level: ConsoleLevel): void {
    this._enabledLevels.update(set => {
      const next = new Set(set);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return next;
    });
    this.persistEnabledLevels();
  }

  setOnlyLevel(level: ConsoleLevel | null): void {
    if (level === null) {
      this._enabledLevels.set(new Set(CONSOLE_LEVELS));
    } else {
      this._enabledLevels.set(new Set([level]));
    }
    this.persistEnabledLevels();
  }

  setAllLevels(enabled: boolean): void {
    this._enabledLevels.set(enabled ? new Set(CONSOLE_LEVELS) : new Set());
    this.persistEnabledLevels();
  }

  // ── Enabled-levels persistence ──────────────────────────────────────────────

  private loadEnabledLevels(): Set<ConsoleLevel> {
    if (environment.production) return new Set(CONSOLE_LEVELS);
    const raw = localStorage.getItem(KEY_ENABLED_LEVELS);
    if (!raw) return new Set(CONSOLE_LEVELS);
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Set(CONSOLE_LEVELS);
      const valid = parsed.filter((v): v is ConsoleLevel =>
        CONSOLE_LEVELS.includes(v as ConsoleLevel)
      );
      return new Set(valid);
    } catch {
      return new Set(CONSOLE_LEVELS);
    }
  }

  private persistEnabledLevels(): void {
    try {
      localStorage.setItem(KEY_ENABLED_LEVELS, JSON.stringify([...this._enabledLevels()]));
    } catch {
      /* quota — ignore */
    }
  }

  // ── Hook lifecycle ──────────────────────────────────────────────────────────

  private hook(): void {
    if (this.isHooked) return;
    this.isHooked = true;

    for (const level of CONSOLE_LEVELS) {
      const original = (console as unknown as Record<ConsoleLevel, ConsoleMethod>)[level];
      this.originals[level] = original;

      const wrapper: ConsoleMethod = (...args: unknown[]) => {
        // Capture the stack AT the wrapper entry so the caller is always a
        // fixed offset away — no fragile substring filters against class
        // names that would change under minification / bundling.
        const callSite = new Error();
        // Always forward to the original first so the browser DevTools still
        // shows the log (even if the source line now points to this wrapper).
        try {
          original.apply(console, args);
        } catch {
          /* ignore — never let the dev-tools panel break the app */
        }
        try {
          this.capture(level, args, callSite);
        } catch {
          /* ignore capture errors to avoid feedback loops */
        }
      };

      (console as unknown as Record<ConsoleLevel, ConsoleMethod>)[level] = wrapper;
    }
  }

  private unhook(): void {
    if (!this.isHooked) return;
    this.isHooked = false;

    for (const level of CONSOLE_LEVELS) {
      const original = this.originals[level];
      if (original) {
        (console as unknown as Record<ConsoleLevel, ConsoleMethod>)[level] = original;
      }
    }
    this.originals = {};
  }

  // ── Capture helpers ─────────────────────────────────────────────────────────

  private capture(level: ConsoleLevel, args: unknown[], callSite: Error): void {
    const serialized = args.map(a => this.serializeArg(a));

    // De-duplicate within the current flush window: if the exact same level +
    // args (ignoring stack traces) were already captured, suppress. This breaks
    // NG0100 feedback loops where our signal update → CD → same error again.
    const dedupeKey = level + ':' + this.quickKey(serialized);
    if (this.recentKeys.has(dedupeKey)) return;
    this.recentKeys.add(dedupeKey);

    const entry: ConsoleEntry = {
      id: crypto.randomUUID(),
      level,
      args: serialized,
      timestamp: Date.now(),
      source: this.extractSource(callSite, args),
    };
    this.buffer.push(entry);

    if (!this.flushScheduled) {
      this.flushScheduled = true;
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.flushScheduled = false;
          this.recentKeys.clear();
          this.flushBuffer();
        }, FLUSH_INTERVAL_MS);
      });
    }
  }

  private flushBuffer(): void {
    const batch = this.buffer.splice(0);
    if (batch.length === 0) return;
    this._entries.update(list => {
      const next = [...list, ...batch];
      return next.length > MAX_ENTRIES ? next.slice(-MAX_ENTRIES) : next;
    });
  }

  /** Fast key for dedup — JSON with stack traces stripped. */
  private quickKey(args: unknown[]): string {
    try {
      return JSON.stringify(args, (k, v) => k === 'stack' ? undefined : v);
    } catch {
      return '';
    }
  }

  /** Convert Error / HttpErrorResponse objects to plain serializable objects at capture time. */
  private serializeArg(arg: unknown): unknown {
    if (arg === null || arg === undefined || typeof arg !== 'object') return arg;
    if (arg instanceof Error){

      return this.errorToPlain(arg);
    }
    // HttpErrorResponse (doesn't extend Error, but has error + status)
    if ('error' in arg && 'status' in arg && 'message' in arg) {
      const obj = arg as Record<string, unknown>;
      return {
        _type: obj['name'] ?? 'HttpErrorResponse',
        status: obj['status'],
        statusText: obj['statusText'],
        url: obj['url'],
        message: obj['message'],
        error: this.serializeArg(obj['error']),
        ok: obj['ok'],
      };
    }
    return arg;
  }

  private errorToPlain(err: Error): Record<string, unknown> {
    const obj: Record<string, unknown> = {
      _type: err.name || '',
      message: err.message,
    };
    for (const key of Object.keys(err)) {
      if (key !== 'name' && key !== 'message' && key !== 'stack') {
        obj[key] = this.serializeArg((err as any)[key]);
      }
    }
    if (err.stack) obj['stack'] = err.stack;
    if (err.cause) {
      obj['cause'] = err.cause instanceof Error ? this.errorToPlain(err.cause) : err.cause;
    }
    return obj;
  }

  /** Picks the most informative frame for a log call.
   *
   *  Strategy:
   *  1. If any arg is an Error (or wraps one), its `.stack` was captured at
   *     throw time and points to the real origin — prefer that.
   *  2. Otherwise use the wrapper's synthetic stack (skip 1 frame: the wrapper).
   *  3. In both cases, skip framework frames (Angular chunks, node_modules,
   *     zone.js) until we find app code. If nothing qualifies, return the
   *     best-available frame so the cell is never empty.
   */
  private extractSource(callSite: Error, args: unknown[]): string | undefined {
    const errorStack = this.findStackInArgs(args);

    // DEBUG: dump both stacks so they can be inspected from the browser console
    // via `copy(localStorage.getItem('devBar.console.lastStack'))`.
    try {
      const dump =
        `=== wrapper stack ===\n${callSite.stack ?? '(none)'}\n\n` +
        `=== error-arg stack ===\n${errorStack ?? '(none)'}`;
      localStorage.setItem('devBar.console.lastStack', dump);
    } catch { /* quota */ }

    if (errorStack) {
      const frame = this.firstAppFrame(errorStack, 0);
      if (frame) return frame;
    }
    if (callSite.stack) {
      const frame = this.firstAppFrame(callSite.stack, 1);
      if (frame) return frame;
    }
    return undefined;
  }

  /** Recursively search args for the first Error-like object with a stack. */
  private findStackInArgs(args: unknown[]): string | undefined {
    for (const arg of args) {
      const stack = this.stackFromArg(arg, 0);
      if (stack) return stack;
    }
    return undefined;
  }

  private stackFromArg(arg: unknown, depth: number): string | undefined {
    if (depth > 3 || !arg || typeof arg !== 'object') return undefined;
    if (arg instanceof Error && typeof arg.stack === 'string') return arg.stack;
    const obj = arg as { stack?: unknown; error?: unknown; cause?: unknown };
    if (typeof obj.stack === 'string' && obj.stack.includes('\n')) return obj.stack;
    return this.stackFromArg(obj.error, depth + 1)
      ?? this.stackFromArg(obj.cause, depth + 1);
  }

  /** Walk a stack, skipping `skipInitial` frames and framework frames, and
   *  return the first remaining frame formatted. Falls back to the first frame
   *  seen (post-skip) if nothing looks like app code. */
  private firstAppFrame(stack: string, skipInitial: number): string | undefined {
    const lines = stack.split('\n');
    let skipped = 0;
    let fallback: string | undefined;
    for (const rawLine of lines) {
      const line = rawLine.trim();
      if (!line) continue;
      if (line === 'Error' || /^Error:?\s/.test(line)) continue;
      if (skipped < skipInitial) { skipped++; continue; }
      if (!fallback) fallback = line;
      if (this.isFrameworkFrame(line)) continue;
      return this.formatStackFrame(line);
    }
    return fallback ? this.formatStackFrame(fallback) : undefined;
  }

  /** Heuristic: frames we'd rather skip to reach real app code. */
  private isFrameworkFrame(line: string): boolean {
    if (/\/vite\/deps\/chunk-/i.test(line)) return true;
    if (/\/node_modules\//i.test(line)) return true;
    if (/\bzone\.js\b/i.test(line)) return true;
    if (/\/@angular\//i.test(line)) return true;
    return false;
  }

  /** Turn a raw stack frame into "ClassName.method (file:line:col)". */
  private formatStackFrame(frame: string): string {
    let fn: string | undefined;
    let loc: string;
    const v8WithFn = frame.match(/^at\s+(.+?)\s+\((.+)\)\s*$/);
    if (v8WithFn) {
      fn = v8WithFn[1];
      loc = v8WithFn[2];
    } else {
      const v8NoFn = frame.match(/^at\s+(.+)$/);
      if (v8NoFn) {
        loc = v8NoFn[1];
      } else {
        const firefox = frame.match(/^(.+?)@(.+)$/);
        if (firefox) { fn = firefox[1]; loc = firefox[2]; }
        else { loc = frame; }
      }
    }
    // Drop "?v=..." query string and "#hash" that Vite appends to bundle URLs.
    const noQuery = loc.replace(/\?[^:]*(?=:\d+(?::\d+)?$)/, '');
    const short = noQuery.match(/([^/\\]+?):(\d+)(?::(\d+))?$/);
    const cleanLoc = short
      ? `${short[1]}:${short[2]}${short[3] ? ':' + short[3] : ''}`
      : noQuery;
    return fn ? `${fn} (${cleanLoc})` : cleanLoc;
  }
}
